export type CompatibilityProfile = {
  movement_id: string | null
  compatible_movement_ids: string[]
  case_diameter: number | null
  lug_width: number | null
  dial_diameter: number | null
  water_resistance: number | null
  hand_hole_sizes: Record<string, number> | null
  case_models: string[]
  crown_diameter: number | null
  crown_thickness: number | null
  stem_type: string | null
  crown_included: boolean | null
}

export type WatchPart = {
  id: string
  name: string
  type: string
  material: string | null
  price: number
  currency: string
  image_url: string | null
  description: string | null
  provider_id: string
  purchase_url: string | null
  compatibility: CompatibilityProfile
  metadata: Record<string, unknown>
}

export type SelectionDecision = {
  part_type: string
  selected_part: WatchPart
  candidate_count: number
  reason: string
  rejected_parts: Array<{
    part_id: string
    name: string
    reason: string
  }>
}

export type WatchSizeDecision = {
  selected_size: string
  min_case_diameter: number
  max_case_diameter: number
  reason: string
}

export type WatchBuildResult = {
  build: {
    parts: Record<string, WatchPart>
  }
  watch_size: WatchSizeDecision
  decisions: SelectionDecision[]
  compatibility_errors: string[]
  failed_steps: BuildStepFailure[]
}

export type BuildStepFailure = {
  part_type: string
  reason: string
  rejected_parts: Array<{ part_id: string; name: string; reason: string }>
  available_alternatives: string[]
  suggestion: string
}

export type ChatMessage = {
  id: string | null
  conversation_id: string
  sequence: number | null
  role: string
  content: string
  raw_message: Record<string, unknown> | null
}

export type ConversationRecord = {
  id: string
  agent_prompt_id: string
  title: string | null
  status: string
  metadata: Record<string, unknown>
}

export type ConversationMessagesResponse = {
  conversation_id: string
  messages: ChatMessage[]
}

export type SaveConversationBuildResponse = {
  conversation_id: string
  saved_at: string
  build_result: WatchBuildResult
}

export type ChatTurnResponse = {
  conversation_id: string
  assistant_message: string
  build_result: WatchBuildResult | null
  messages: ChatMessage[]
}

export type ChatTurnRequest = {
  message: string
  conversation_id?: string
  prompt_name?: string
  metadata?: Record<string, unknown>
}

export type BuildSlotStatus = 'pending' | 'locked' | 'searching' | 'selected' | 'failed'

export type BuildSlotProgress = {
  status: BuildSlotStatus
  part_id: string | null
  part_name: string | null
}

export type BuildProgressSnapshot = {
  phase: 'sizing' | 'building' | 'resolve_conflicts' | 'finalizing'
  label: string
  active_slot: string | null
  slots: Record<string, BuildSlotProgress>
  watch_size_selected: string | null
}

export type ChatStreamEventType =
  | 'status'
  | 'build_progress'
  | 'assistant_message'
  | 'build_result'
  | 'error'
  | 'done'

export type ChatStreamEvent = {
  type: ChatStreamEventType
  label?: string | null
  progress?: BuildProgressSnapshot | null
  message?: string | null
  conversation_id?: string | null
  build_result?: WatchBuildResult | null
}

export type ChatStreamResult = {
  conversation_id: string
  assistant_message: string
  build_result: WatchBuildResult | null
}

export type PipelineStepTrace = {
  name: string
  status: 'success' | 'failed' | 'skipped'
  duration_ms: number
  input_data: Record<string, unknown>
  output_data: Record<string, unknown>
  item_count: number | null
  error_message: string | null
}

export type PipelineTrace = {
  steps: PipelineStepTrace[]
  total_duration_ms: number
  query: Record<string, unknown> | null
  results_count: number
}

export type DebugRetrievalResponse = {
  results: WatchPart[]
  trace: PipelineTrace
}

export type AgentTraceStep = {
  step_type: 'reasoning' | 'tool_call' | 'tool_result'
  timestamp: number | null
  content: string | null
  tool_name: string | null
  arguments: Record<string, unknown> | null
  result: unknown | null
  duration_ms: number | null
}

export type AgentTrace = {
  search_brief: Record<string, unknown> | null
  system_prompt: string | null
  steps: AgentTraceStep[]
  final_response: string | null
}

export type DebugChatTurnResponse = {
  conversation_id: string
  assistant_message: string
  build_result: WatchBuildResult | null
  messages: ChatMessage[]
  agent_trace: AgentTrace
}

export type WatchImageResult = {
  image_url: string
  prompt_used: string
  build_fingerprint: string
  cached: boolean
}

const API_BASE_URL =
  import.meta.env.VITE_AUREL_API_URL?.replace(/\/$/, '') ?? 'http://localhost:8000'

export type HealthResponse = {
  status: string
  debug_ui: boolean
  catalog_admin_ui: boolean
}

export const CATALOG_ADMIN_KEY_STORAGE = 'aurel_catalog_admin_key'

export function getCatalogAdminKey(): string | null {
  return sessionStorage.getItem(CATALOG_ADMIN_KEY_STORAGE)
}

export function setCatalogAdminKey(key: string): void {
  sessionStorage.setItem(CATALOG_ADMIN_KEY_STORAGE, key)
}

export function clearCatalogAdminKey(): void {
  sessionStorage.removeItem(CATALOG_ADMIN_KEY_STORAGE)
}

function catalogAdminHeaders(extra: HeadersInit = {}): HeadersInit {
  const key = getCatalogAdminKey()
  return {
    ...extra,
    ...(key ? { 'X-Aurel-Catalog-Admin-Key': key } : {}),
  }
}

export type CatalogAdminPartSummary = {
  part: WatchPart
  has_override: boolean
  overridden_paths: string[]
}

export type CatalogAdminSearchResponse = {
  results: CatalogAdminPartSummary[]
}

export type CatalogAdminPartDetail = {
  part: WatchPart
  patch: Record<string, unknown>
  note: string | null
  overridden_paths: string[]
}

export async function fetchHealth(): Promise<HealthResponse> {
  const response = await fetch(`${API_BASE_URL}/health`)
  return handleResponse<HealthResponse>(response)
}

async function handleResponse<T>(response: Response): Promise<T> {
  if (!response.ok) {
    let message = `Horatix API returned ${response.status}`

    try {
      const errorBody = (await response.json()) as { detail?: unknown }
      if (typeof errorBody.detail === 'string') {
        message = errorBody.detail
      }
    } catch {
      // Keep the status-based message when the API does not return JSON.
    }

    throw new Error(message)
  }

  return response.json() as Promise<T>
}

export async function sendChatTurn(request: ChatTurnRequest): Promise<ChatTurnResponse> {
  const response = await fetch(`${API_BASE_URL}/chat`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      prompt_name: 'part_finder',
      metadata: {},
      ...request,
    }),
  })

  return handleResponse<ChatTurnResponse>(response)
}

export type SendChatTurnStreamOptions = {
  signal?: AbortSignal
  onEvent?: (event: ChatStreamEvent) => void
}

export async function sendChatTurnStream(
  request: ChatTurnRequest,
  options: SendChatTurnStreamOptions = {},
): Promise<ChatStreamResult> {
  const response = await fetch(`${API_BASE_URL}/chat/stream`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'text/event-stream',
    },
    body: JSON.stringify({
      prompt_name: 'part_finder',
      metadata: {},
      ...request,
    }),
    signal: options.signal,
  })

  if (!response.ok) {
    await handleResponse<never>(response)
  }

  const reader = response.body?.getReader()
  if (!reader) {
    throw new Error('Streaming response body is not available.')
  }

  const decoder = new TextDecoder()
  let buffer = ''
  let conversationId = request.conversation_id ?? ''
  let assistantMessage = ''
  let buildResult: WatchBuildResult | null = null

  const processLine = (line: string) => {
    if (!line.startsWith('data:')) return
    const payload = line.slice(5).trim()
    if (!payload) return

    const event = JSON.parse(payload) as ChatStreamEvent
    options.onEvent?.(event)

    if (event.conversation_id) {
      conversationId = event.conversation_id
    }
    if (event.type === 'assistant_message' && event.message) {
      assistantMessage = event.message
    }
    if (event.type === 'build_result' && event.build_result) {
      buildResult = event.build_result
    }
    if (event.type === 'error' && event.message) {
      throw new Error(event.message)
    }
  }

  while (true) {
    const { done, value } = await reader.read()
    if (done) break

    buffer += decoder.decode(value, { stream: true })
    const lines = buffer.split('\n')
    buffer = lines.pop() ?? ''

    for (const line of lines) {
      processLine(line.trim())
    }
  }

  if (buffer.trim()) {
    processLine(buffer.trim())
  }

  if (!conversationId) {
    throw new Error('Stream ended without a conversation id.')
  }

  return {
    conversation_id: conversationId,
    assistant_message: assistantMessage,
    build_result: buildResult,
  }
}

export async function sendDebugRetrieval(
  raw_text?: string,
  part_type?: string,
  constraints?: Record<string, unknown>
): Promise<DebugRetrievalResponse> {
  const response = await fetch(`${API_BASE_URL}/debug/retrieval`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      raw_text: raw_text || null,
      part_type: part_type || null,
      constraints: constraints || {},
    }),
  })

  return handleResponse<DebugRetrievalResponse>(response)
}

export async function sendDebugChatTurn(request: ChatTurnRequest): Promise<DebugChatTurnResponse> {
  const response = await fetch(`${API_BASE_URL}/debug/agent/chat`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      prompt_name: 'part_finder',
      metadata: {},
      ...request,
    }),
  })

  return handleResponse<DebugChatTurnResponse>(response)
}

export async function parseSearchBrief(
  message: string,
  metadata?: Record<string, unknown>
): Promise<Record<string, unknown>> {
  const response = await fetch(`${API_BASE_URL}/debug/search-brief`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      message,
      metadata: metadata || {},
    }),
  })

  return handleResponse<Record<string, unknown>>(response)
}

export async function fetchCatalogAdminParts(params: {
  text?: string
  part_type?: string
  provider_id?: string
  available_only?: boolean
  limit?: number
  offset?: number
}): Promise<CatalogAdminSearchResponse> {
  const query = new URLSearchParams()
  if (params.text) query.set('text', params.text)
  if (params.part_type) query.set('part_type', params.part_type)
  if (params.provider_id) query.set('provider_id', params.provider_id)
  if (params.available_only) query.set('available_only', 'true')
  if (params.limit !== undefined) query.set('limit', String(params.limit))
  if (params.offset !== undefined) query.set('offset', String(params.offset))

  const response = await fetch(`${API_BASE_URL}/catalog-admin/parts?${query}`, {
    headers: catalogAdminHeaders(),
  })
  return handleResponse<CatalogAdminSearchResponse>(response)
}

export async function getCatalogAdminPart(partId: string): Promise<CatalogAdminPartDetail> {
  const response = await fetch(
    `${API_BASE_URL}/catalog-admin/parts/${encodeURIComponent(partId)}`,
    { headers: catalogAdminHeaders() },
  )
  return handleResponse<CatalogAdminPartDetail>(response)
}

export async function saveCatalogAdminOverride(
  partId: string,
  body: { patch: Record<string, unknown>; note?: string | null },
): Promise<CatalogAdminPartDetail> {
  const response = await fetch(
    `${API_BASE_URL}/catalog-admin/parts/${encodeURIComponent(partId)}/override`,
    {
      method: 'PUT',
      headers: catalogAdminHeaders({ 'Content-Type': 'application/json' }),
      body: JSON.stringify(body),
    },
  )
  return handleResponse<CatalogAdminPartDetail>(response)
}

export async function clearCatalogAdminOverride(
  partId: string,
): Promise<CatalogAdminPartDetail> {
  const response = await fetch(
    `${API_BASE_URL}/catalog-admin/parts/${encodeURIComponent(partId)}/override`,
    {
      method: 'DELETE',
      headers: catalogAdminHeaders(),
    },
  )
  return handleResponse<CatalogAdminPartDetail>(response)
}

export async function lookupAssembledImage(
  buildResult: WatchBuildResult,
  options: { signal?: AbortSignal } = {},
): Promise<WatchImageResult | null> {
  const response = await fetch(`${API_BASE_URL}/builds/lookup-image`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      build: buildResult.build,
    }),
    signal: options.signal,
  })

  if (response.status === 404) {
    return null
  }

  return handleResponse<WatchImageResult>(response)
}

export async function generateAssembledImage(
  buildResult: WatchBuildResult,
): Promise<WatchImageResult> {
  const response = await fetch(`${API_BASE_URL}/builds/generate-image`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      build: buildResult.build,
    }),
  })

  return handleResponse<WatchImageResult>(response)
}

export async function fetchConversation(conversationId: string): Promise<ConversationRecord> {
  const response = await fetch(
    `${API_BASE_URL}/conversations/${encodeURIComponent(conversationId)}`,
  )
  return handleResponse<ConversationRecord>(response)
}

export async function fetchConversationMessages(
  conversationId: string,
): Promise<ConversationMessagesResponse> {
  const response = await fetch(
    `${API_BASE_URL}/conversations/${encodeURIComponent(conversationId)}/messages`,
  )
  return handleResponse<ConversationMessagesResponse>(response)
}

export async function saveConversationBuild(
  conversationId: string,
  buildResult: WatchBuildResult,
): Promise<SaveConversationBuildResponse> {
  const response = await fetch(
    `${API_BASE_URL}/conversations/${encodeURIComponent(conversationId)}/build`,
    {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ build_result: buildResult }),
    },
  )
  return handleResponse<SaveConversationBuildResponse>(response)
}

export function extractBuildResult(
  metadata: Record<string, unknown> | null | undefined,
): WatchBuildResult | null {
  const raw = metadata?.previous_build_result
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null
  const candidate = raw as Partial<WatchBuildResult>
  if (!candidate.build || typeof candidate.build !== 'object') return null
  if (!candidate.build.parts || typeof candidate.build.parts !== 'object') return null
  return candidate as WatchBuildResult
}
